import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { probationApi } from '../../api/probation';
import { ApiError } from '../../api/client';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye } from 'lucide-react';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { RecordDetailModal } from '../../components/RecordDetailModal';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress', under_review: 'Awaiting HR Decision', confirmed: 'Confirmed', extended: 'Extended', non_confirmed: 'Not Confirmed',
};

function ProbationDetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const detailQuery = useQuery({ queryKey: ['probation-detail', id], queryFn: () => probationApi.getById(id) });
  if (!detailQuery.data) return <RecordDetailModal title="Loading…" fields={[]} onClose={onClose} />;
  const { data: record, objectives, reviews } = detailQuery.data;

  return (
    <RecordDetailModal
      title={`Probation Record - ${record.employee_name ?? ''}`}
      onClose={onClose}
      fields={[
        { label: 'Status', value: <StatusBadge status={record.status} label={STATUS_LABELS[record.status]} /> },
        { label: 'Start Date', value: record.start_date },
        { label: 'Expected End Date', value: record.expected_end_date },
        { label: 'Extended End Date', value: record.extension_new_end_date },
        { label: 'Supervisor', value: record.supervisor_name },
        { label: 'Supervisor Recommendation', value: record.supervisor_recommendation },
        { label: 'Supervisor Comments', value: record.supervisor_recommendation_comments, fullWidth: true },
        { label: 'HR Decision', value: record.hr_decision },
        { label: 'HR Decision Comments', value: record.hr_decision_comments, fullWidth: true },
      ]}
    >
      {objectives.length > 0 && (
        <div style={{ marginTop: '1em' }}>
          <div className="record-detail-label">Objectives</div>
          <ul>{objectives.map((o) => <li key={o.id}>{o.objective}</li>)}</ul>
        </div>
      )}
      {reviews.length > 0 && (
        <div style={{ marginTop: '1em' }}>
          <div className="record-detail-label">Reviews</div>
          {reviews.map((r) => (
            <div key={r.id} className="record-detail-row-full">
              <div className="record-detail-value"><strong>{r.review_date}</strong> by {r.submitted_by_name} - {r.assessment}</div>
            </div>
          ))}
        </div>
      )}
    </RecordDetailModal>
  );
}

/** Real HR monitoring - alerts are genuinely computed from actual dates, not placeholders, matching the spec's "approaching expiry / overdue" requirement. */
export function ProbationPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [viewingId, setViewingId] = useState<number | null>(null);

  const alertsQuery = useQuery({ queryKey: ['probation-alerts'], queryFn: () => probationApi.alerts() });
  const allQuery = useQuery({ queryKey: ['probation-all'], queryFn: () => probationApi.listAll() });

  const { register, handleSubmit, reset } = useForm<{ employeeId: string; startDate: string; expectedEndDate: string; objectives: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { employeeId: string; startDate: string; expectedEndDate: string; objectives: string }) =>
      probationApi.create({ employeeId: Number(v.employeeId), startDate: v.startDate, expectedEndDate: v.expectedEndDate, objectives: v.objectives ? v.objectives.split('\n').filter(Boolean) : [] }),
    onSuccess: () => { showSuccess('Probation record started.'); queryClient.invalidateQueries({ queryKey: ['probation-all'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to start probation.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <h1>Probation &amp; Confirmation</h1>

      {alertsQuery.data && (alertsQuery.data.data.expiringSoon.length > 0 || alertsQuery.data.data.overdue.length > 0) && (
        <section className="card">
          <h2>Alerts</h2>
          {alertsQuery.data.data.overdue.length > 0 && (
            <p className="muted" style={{ color: 'var(--color-danger)' }}>
              {alertsQuery.data.data.overdue.length} probation review(s) overdue: {alertsQuery.data.data.overdue.map((r) => r.employee_name).join(', ')}
            </p>
          )}
          {alertsQuery.data.data.expiringSoon.length > 0 && (
            <p className="muted">
              {alertsQuery.data.data.expiringSoon.length} probation(s) ending within 30 days: {alertsQuery.data.data.expiringSoon.map((r) => r.employee_name).join(', ')}
            </p>
          )}
        </section>
      )}

      <section className="card">
        <h2>Start New Probation</h2>
        <form className="inline-form" onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
          <label>Employee ID<input type="number" {...register('employeeId', { required: true })} /></label>
          <label>Start Date<input type="date" {...register('startDate', { required: true })} /></label>
          <label>Expected End Date<input type="date" {...register('expectedEndDate', { required: true })} /></label>
          <label>Objectives (one per line)<textarea rows={2} {...register('objectives')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Start Probation</button>
        </form>
      </section>

      <section className="card">
        <h2>All Probation Records</h2>
        {allQuery.data && (
          <DataTable
            rows={allQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No probation records yet."
            columns={[
              { header: 'Employee', render: (r) => r.employee_name },
              { header: 'Start', render: (r) => r.start_date },
              { header: 'Expected End', render: (r) => r.expected_end_date },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => (
                <span className="row-actions">
                  <IconActionButton icon={Eye} label="View" onClick={() => setViewingId(r.id)} />
                  <Link to={`/hr/staff/${r.employee_id}`}>View in Staff Detail</Link>
                </span>
              ) },
            ]}
          />
        )}
      </section>

      {viewingId !== null && <ProbationDetailModal id={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
}
